import { useCallback, useRef, useState } from "react";
import { AlertCircle, FolderOpen, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useBulkUpload } from "@/contexts/uploads/BulkUploadContext";
import { cn } from "@/lib/utils";
import { ACCEPTED_EXTENSIONS, ACCEPTED_TYPES } from "@/lib/fileType";
import BulkUploadFileItem from "./BulkUploadFileItem";

const BulkUploadDropzone = () => {
  const { state, addFiles, removeFile, startUpload, openPanel } = useBulkUpload();

  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const pendingFiles = state.files.filter((f) => f.status === "pending");

  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      const result = addFiles(files);

      // Show validation feedback
      if (result.errors.length > 0) {
        result.errors.forEach((error) => toast.error(error));
      }

      if (result.warnings.length > 0) {
        result.warnings.forEach((warning) => toast.warning(warning));
      }

      if (result.invalidFiles.length > 0) {
        result.invalidFiles.forEach(({ file, reason }) => toast.error(`${file.name}: ${reason}`));
      }

      if (result.validFiles.length > 0) {
        toast.success(`Added ${result.validFiles.length} files to queue`);
      }
    },
    [addFiles],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFiles(files);
      }
    },
    [handleFiles],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFiles(files);
      }
      // Reset input
      e.target.value = "";
    },
    [handleFiles],
  );

  const handleStartUpload = () => {
    if (pendingFiles.length === 0) {
      toast.info("No files to upload");
      return;
    }
    startUpload();
    openPanel();
  };

  return (
    <div className="space-y-6">
      {/* Dropzone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "relative border-2 border-dashed rounded-xl p-8 transition-all duration-200 text-center",
          isDragging ? "border-genie-primary bg-genie-primary/5" : "border-gray-300 hover:border-genie-primary/50",
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={`${ACCEPTED_EXTENSIONS},${ACCEPTED_TYPES}`}
          onChange={handleFileInputChange}
          className="hidden"
        />

        <input
          ref={folderInputRef}
          type="file"
          // @ts-expect-error webkitdirectory is not in the type definitions
          webkitdirectory=""
          directory=""
          multiple
          onChange={handleFileInputChange}
          className="hidden"
        />

        <div className="flex flex-col items-center">
          <div className="w-16 h-16 rounded-full bg-genie-primary/10 flex items-center justify-center mb-4">
            <Upload className={cn("h-8 w-8", isDragging ? "text-genie-primary" : "text-gray-400")} />
          </div>

          <p className="text-lg font-medium text-gray-900 mb-2">{isDragging ? "Drop files here" : "Drag and drop files here"}</p>

          <p className="text-sm text-gray-500 mb-4">or use the buttons below to select files</p>

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
              <Upload className="h-4 w-4 mr-2" />
              Select Files
            </Button>

            <Button variant="outline" onClick={() => folderInputRef.current?.click()}>
              <FolderOpen className="h-4 w-4 mr-2" />
              Select Folder
            </Button>
          </div>

          <p className="text-xs text-gray-400 mt-4">Supported: PDF, DOC, DOCX, TXT, CSV, JPG, PNG, WebP</p>
        </div>
      </div>

      {/* Validation Info */}
      <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
        <AlertCircle className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-gray-700">Upload limits</p>
          <p>Maximum 50 files per batch. Large files (over 20MB) may take longer to process.</p>
        </div>
      </div>

      {/* Preview Queue */}
      {pendingFiles.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">{pendingFiles.length} files ready to upload</h3>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {pendingFiles.map((file) => (
              <BulkUploadFileItem key={file.id} file={file} onRemove={removeFile} />
            ))}
          </div>

          <Button
            onClick={handleStartUpload}
            className="w-full bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200"
          >
            <Upload className="h-4 w-4 mr-2" />
            Start Upload ({pendingFiles.length} files)
          </Button>
        </div>
      )}
    </div>
  );
};

export default BulkUploadDropzone;
